import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.tsx';
import { cn } from '@/utils/utils.ts';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area.tsx';
import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { LastUpdateTimestamp } from '@/components/last-update-timestamp.tsx';
import { useGetEventsQuery } from '@/features/dashboard/hooks/use-get-events.ts';
import { useInView } from 'react-intersection-observer';
import { useEffect } from 'react';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { EventsMoreSettingsPopover } from '@/features/dashboard/components/events/events-more-options-popover.tsx';
import { useEventsFilterStore } from '@/services/store/use-events-store.ts';
import { useDebounce } from '@/hooks/use-debounce.ts';

export const Events = ({ className }: { className?: string }) => {
  const { t } = useTranslation();
  const { ref: scrollRef, inView } = useInView();

  const {
    data: events,
    fetchNextPage,
    isFetchingNextPage,
    dataUpdatedAt,
    isLoading,
    hasNextPage,
  } = useGetEventsQuery();

  const debouncedEventsPagesLength = useDebounce(events?.pages.length, 600);

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage().then();
    }
  }, [debouncedEventsPagesLength, inView, hasNextPage]);

  const eventColorMap = {
    Info: '#93c5fd',
    Major: '#fca5a5',
  };

  const sourceFilters = useEventsFilterStore((state) => state.sourceFilters);

  const filteredEvents =
    sourceFilters.length === 0
      ? events?.pages
      : events?.pages?.filter((r) => {
          return Boolean(sourceFilters.find((source) => source.sourceId === r.sourceId));
        });

  return (
    <DashboardCard
      isLoading={isLoading}
      className={cn('max-h-[400px] min-h-[200px] gap-1', className)}
    >
      <>
        <div className="flex justify-between">
          <div className="flex w-full flex-col">
            <h3 className="font-semibold text-nowrap">{t('dashboard.events.title')}</h3>
            <LastUpdateTimestamp updatedAt={dataUpdatedAt} updateInterval={10000} />
          </div>
          <EventsMoreSettingsPopover />
        </div>

        <ScrollArea className="flex h-full w-full">
          <Table>
            <TableHeader className="bg-background sticky top-0 z-10">
              <TableRow>
                <TableHead>{t('common.type.date')}</TableHead>
                <TableHead>{t('common.type.type')}</TableHead>
                <TableHead>{t('common.type.message')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEvents?.length === 0 && (
                <TableRow className="border-0 pt-4 pl-2">
                  <TableCell> {t('dashboard.events.no_events')}</TableCell>
                </TableRow>
              )}
              {Boolean(filteredEvents?.length) &&
                filteredEvents?.map((event) => {
                  return (
                    <TableRow key={event?.eventId}>
                      <TableCell className="h-[45px] text-nowrap">
                        {event?.createdAt && format(event?.createdAt, 'dd/MM/yyyy HH:mm')}
                      </TableCell>
                      <TableCell className="h-[45px] text-nowrap">
                        <span
                          className="rounded-lg px-2 py-0.5"
                          style={{
                            backgroundColor: `${eventColorMap[event?.type as 'Info' | 'Major']}`,
                          }}
                        >
                          {event?.type}
                        </span>
                      </TableCell>
                      <TableCell className="min-h-[45px]">{event?.description}</TableCell>
                    </TableRow>
                  );
                })}
              <TableRow ref={scrollRef} className="h-px border-0 pt-4 pb-2">
                {((inView && hasNextPage) || isFetchingNextPage) && (
                  <TableCell colSpan={3}>
                    <div className="flex w-full items-center justify-center py-4">
                      <LoadingSpinner className="text-muted-foreground size-10" />
                    </div>
                  </TableCell>
                )}
              </TableRow>
            </TableBody>
          </Table>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </>
    </DashboardCard>
  );
};
